import {
  Campaign,
  CampaignProduct,
  CampaignProductColor,
  CampaignStatusVariant,
  CreatorCampaignApiItem,
  CreatorCampaignDetailApiItem,
  CreatorCampaignDetailPopulatedProduct,
  CreatorCampaignPopulatedProduct,
} from '@/types/campaign'
import dayjs from 'dayjs'

const COLOR_OPTIONS_ID = 'color'

const STATUS_META: Record<
  Campaign['status'],
  { label: string; variant?: CampaignStatusVariant }
> = {
  draft: { label: 'Bản nháp' },
  upcoming: { label: 'Sắp mở đăng ký', variant: 'attention' },
  registration_open: { label: 'Đang mở đăng ký', variant: 'success' },
  registration_closed: { label: 'Đã đóng đăng ký', variant: 'warning' },
  executing: { label: 'Đang triển khai', variant: 'info' },
  closed: { label: 'Đã kết thúc', variant: 'critical' },
  archived: { label: 'Lưu trữ' },
}

const formatDate = (value: string) => dayjs(value).format('DD/MM/YYYY')

const mapBaseProduct = (
  product: CreatorCampaignPopulatedProduct
): CampaignProduct => ({
  id: product.product_id,
  slug: product.slug ?? '',
  name: product.title,
  image: product.image?.src ?? '',
  price: product.compare_price,
  colors: [],
  sizes: [],
  variants: [],
})

interface ColorImages {
  readonly thumbnail: string
  readonly image: string
}

// Ảnh theo màu lấy từ options_value (nguồn ảnh thật của từng màu), không dùng
// variant.image (thường rỗng) hay ảnh chung sản phẩm để tránh sai lệch.
// thumbnail: ảnh đầu tiên (đổi ảnh lớn), image: ảnh cuối cùng (nền swatch) -
// theo đúng cách listing sản phẩm đang xử lý ở __f.product.ts.
const getColorImageMap = (
  optionsValue: CreatorCampaignDetailPopulatedProduct['options_value']
): Map<string, ColorImages> => {
  const colorGroup = optionsValue.find(
    (group) => group?.options_id === COLOR_OPTIONS_ID
  )

  const entries = (colorGroup?.options ?? []).map((option) => {
    const validImages = option.image.filter((img) => img.src)
    const thumbnail = validImages[0]?.src ?? ''
    const image = validImages[validImages.length - 1]?.src ?? thumbnail
    return [option.title, { thumbnail, image }] as const
  })

  return new Map(entries)
}

const mapDetailProduct = (
  product: CreatorCampaignDetailPopulatedProduct
): CampaignProduct => {
  const visibleVariants = product.variants.filter(
    (variant) => !variant.hide && variant.product_visibility && variant.option1
  )

  const colorImageMap = getColorImageMap(product.options_value)

  const colors: CampaignProductColor[] = []
  const seenColors = new Set<string>()
  for (const variant of visibleVariants) {
    if (!seenColors.has(variant.option1)) {
      seenColors.add(variant.option1)
      const colorImages = colorImageMap.get(variant.option1)
      colors.push({
        name: variant.option1,
        image: colorImages?.image ?? '',
        thumbnail: colorImages?.thumbnail ?? '',
      })
    }
  }

  const sizes: string[] = []
  const seenSizes = new Set<string>()
  for (const variant of visibleVariants) {
    if (variant.option2 && !seenSizes.has(variant.option2)) {
      seenSizes.add(variant.option2)
      sizes.push(variant.option2)
    }
  }

  // Giữ lại id thật của từng biến thể color+size để lúc đăng ký resolve ra
  // variant_id gửi lên API, giống hệt cách add-to-cart xử lý.
  const variants = visibleVariants.map((variant) => ({
    id: variant.id,
    color: variant.option1,
    size: variant.option2,
    quantity: variant.quantity,
    available: variant.available,
  }))

  return {
    ...mapBaseProduct(product),
    colors,
    sizes,
    variants,
  }
}

const mapCampaignFields = (
  item: CreatorCampaignApiItem,
  products: readonly CampaignProduct[]
): Campaign => ({
  id: item._id,
  slug: item.slug,
  status: item.status,
  statusLabel: STATUS_META[item.status]?.label ?? STATUS_META.draft.label,
  statusVariant: STATUS_META[item.status]?.variant,
  title: item.name,
  banner: item.banner_url,
  description: item.description,
  registerStart: formatDate(item.registration_start_at),
  registerEnd: formatDate(item.registration_end_at),
  deployStart: formatDate(item.execution_start_at),
  deployEnd: formatDate(item.execution_end_at),
  target: item.target_audience.join(', '),
  gift: item.benefits[0] ?? '',
  tasks: item.tasks,
  benefits: item.benefits,
  productSectionTitle: '01. CHỌN SẢN PHẨM',
  productSectionSubtitle:
    'Chọn sản phẩm bạn muốn trải nghiệm và tham gia chiến dịch',
  products,
})

export const mapCreatorCampaignToCampaign = (
  item: CreatorCampaignApiItem
): Campaign =>
  mapCampaignFields(item, item.populated_products.map(mapBaseProduct))

export const mapCreatorCampaignDetailToCampaign = (
  item: CreatorCampaignDetailApiItem
): Campaign =>
  mapCampaignFields(item, item.populated_products.map(mapDetailProduct))
